import { CWTSchemaList } from '../../../../types/custom-worksheet-v2';
import { isCWTSchemaField, isCWTSchemaSection, isCWTSchemaSectionGroup, sortByOrder } from '../../store/utils';
import { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { addProperty, removeProperty } from '../../store/slice';
import { useDispatch } from 'react-redux';
import Section from './Section';
import SectionGroup from './SectionGroup';
import Header from './Header';
import Table from './Table';
import Field from './field/Field';

type ListProps = {
    list: CWTSchemaList;
};

function List({ list }: ListProps): JSX.Element {
    const dispatch = useDispatch();
    const [isCollapsed, setIsCollapsed] = useState(false);

    function addNewProperty(type: 'SECTION_GROUP' | 'SECTION' | 'TABLE' | 'FIELD'): void {
        dispatch(addProperty({ type, parentId: `${list.id}.properties.item` }));
    }

    function deleteList(): void {
        dispatch(removeProperty(list.id));
    }

    return (
        <div className="w-full flex flex-col">
            {/* List header */}
            <Header
                property={{
                    id: list.id,
                    name: list.name,
                    description: list.description,
                    type: list.type,
                }}
                isCollapsed={isCollapsed}
                onCollapse={setIsCollapsed}
                onDelete={deleteList}
                onCreateTable={(): void => addNewProperty('TABLE')}
                onCreateSection={(): void => addNewProperty('SECTION')}
                onCreateSectionGroup={(): void => addNewProperty('SECTION_GROUP')}
                onCreateField={(): void => addNewProperty('FIELD')}
            />

            {/* Show list container when list is expanded */}
            <AnimatePresence initial={false}>
                {!isCollapsed && (
                    <motion.div
                        exit={{ height: 0, opacity: 0 }}
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        transition={{ height: { duration: 0.2 } }}
                    >
                        <div
                            className={`flex flex-col gap-2 ml-8 ${Object.keys(list.properties.item.properties).length > 0 ? 'pt-2' : ''}`}
                        >
                            {sortByOrder(list.properties.item.properties).map((value) => {
                                const property = value;

                                if (isCWTSchemaSectionGroup(property)) {
                                    return <SectionGroup key={property.id} sectionGroup={property} />;
                                }

                                if (isCWTSchemaSection(property)) {
                                    return <Section key={property.id} section={property} />;
                                }

                                if (isCWTSchemaField(property)) {
                                    return (
                                        <div key={property.id} className="border w-full rounded-lg border-gray-300">
                                            <Field key={property.id} field={property} />
                                        </div>
                                    );
                                }

                                return <Table key={property.id} table={property} />;
                            })}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}

export default List;
